import { useEffect, useMemo, useState } from 'react';
import type { CopilotSession, RegistryEntry } from '@customerio/hydra-copilot';
import { isEmptyString } from '@/utils/string';
import { useCopilotConversations } from '../components/session-provider';
import { useCopilotHistory } from './use-copilot-history';

export type AgentConversationRow = {
  /** Server session id. */
  id: string;
  name: string | null;
  /** Unix seconds; null for live rows the history page doesn't know yet. */
  lastActiveAt: number | null;
  /** Present when the conversation is held live in this tab's registry. */
  liveSession: CopilotSession | null;
};

type HistoryListSession = { id: number; name: string; last_active_at: number };

/** Auto-naming writes the generated name into session meta before the history list refetches. */
function liveSessionName(session: CopilotSession): string | null {
  const metaName: unknown = session.__meta.get()['name'];
  return typeof metaName === 'string' && !isEmptyString(metaName) ? metaName : null;
}

/**
 * Live registry conversations first (most recently activated on top, id-less
 * brand-new sessions skipped), then server history rows, deduped by id with
 * the live entry winning but keeping the server's name.
 */
export function mergeConversationRows(
  entries: ReadonlyArray<RegistryEntry>,
  historySessions: ReadonlyArray<HistoryListSession>,
  limit: number,
): AgentConversationRow[] {
  const historyById = new Map(historySessions.map((session) => [String(session.id), session]));

  const liveRows = entries
    .filter((entry) => entry.session.id != null)
    .sort((a, b) => b.activatedAt - a.activatedAt)
    .map((entry): AgentConversationRow => {
      const id = String(entry.session.id);
      const history = historyById.get(id);
      return {
        id,
        name: history != null && !isEmptyString(history.name) ? history.name : liveSessionName(entry.session),
        lastActiveAt: history?.last_active_at ?? null,
        liveSession: entry.session,
      };
    });

  const liveIds = new Set(liveRows.map((row) => row.id));
  const historyRows = historySessions
    .filter((session) => !liveIds.has(String(session.id)))
    .map(
      (session): AgentConversationRow => ({
        id: String(session.id),
        name: isEmptyString(session.name) ? null : session.name,
        lastActiveAt: session.last_active_at,
        liveSession: null,
      }),
    );

  return [...liveRows, ...historyRows].slice(0, limit);
}

/** Conversations for the floating launcher menu: live ones plus recent history. */
export function useAgentConversations(limit = 5) {
  const { entries } = useCopilotConversations();
  const history = useCopilotHistory();

  // The entries array doesn't re-emit when a session gains its server id
  // (initialize) or its auto-generated name (meta), so subscribe to both —
  // otherwise a fresh conversation never shows up until history refetches.
  const [liveVersion, setLiveVersion] = useState(0);
  useEffect(() => {
    const bump = () => setLiveVersion((version) => version + 1);
    const unsubscribes = entries.flatMap((entry) => [
      entry.session.__id.listen(bump),
      entry.session.__meta.listen(bump),
    ]);
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [entries]);

  const rows = useMemo(() => {
    const historySessions = history.data?.pages.flatMap((page) => page.sessions) ?? [];
    return mergeConversationRows(entries, historySessions, limit);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- liveVersion re-runs the merge when a session's id/meta atom changes
  }, [entries, history.data, limit, liveVersion]);

  return { rows, isLoading: history.isLoading };
}
